import Image from 'next/image';
import clsx from 'clsx';
import { Typography } from '@release/ui/Typography';
import { formatDateCustom } from '@release/shared/utils/format';

import { Greeting, TimeSection } from '../types';

import greetingEvening from '@/features/home/assets/greeting-evening.svg';
import greetingMorning from '@/features/home/assets/greeting-morning.svg';
import greetingNight from '@/features/home/assets/greeting-night.svg';
import greetingNoon from '@/features/home/assets/greeting-noon.svg';

interface HomeGreetingProps {
  greeting: Greeting;
}

// 時間帯ごとの見た目（Figma 26263:34358）。グラデーションはデザイントークン化されていないため生 hex で持つ。
const TIME_SECTION_APPEARANCE: Record<
  TimeSection,
  { background: string; icon: string; textHigh: string; textMedium: string }
> = {
  morning: {
    background: 'bg-gradient-to-r from-[#F7FFFF] to-[#CBFFFF]',
    icon: greetingMorning,
    textHigh: 'text-sem-l-text-high',
    textMedium: 'text-sem-l-text-medium',
  },
  noon: {
    background: 'bg-gradient-to-r from-[#E5FDFF] to-[#B8EEFF]',
    icon: greetingNoon,
    textHigh: 'text-sem-l-text-high',
    textMedium: 'text-sem-l-text-medium',
  },
  evening: {
    background: 'bg-gradient-to-r from-[#B3EDFF] to-[#FFE0B3]',
    icon: greetingEvening,
    textHigh: 'text-sem-l-text-high',
    textMedium: 'text-sem-l-text-medium',
  },
  // 夜はダーク背景のため、3行とも D/Text/High（白）。
  night: {
    background: 'bg-gradient-to-r from-[#0092B3] to-[#002D59]',
    icon: greetingNight,
    textHigh: 'text-sem-d-text-high',
    textMedium: 'text-sem-d-text-high',
  },
};

export const HomeGreeting: React.FC<HomeGreetingProps> = ({ greeting }) => {
  if (!greeting.isReady) {
    return null;
  }
  const formattedDate = formatDateCustom(greeting.date, 'yyyy年M月d日 ({weekday}) H時mm分');
  const appearance = TIME_SECTION_APPEARANCE[greeting.timeSection];
  return (
    <div
      className={clsx(
        'flex flex-row items-center gap-x-2 rounded-md px-4 py-6 sp:px-10 sp:py-8',
        appearance.background
      )}
      data-testid="home-greeting"
    >
      <div className="flex w-full flex-col items-start gap-y-3">
        <Typography size="rabbit" className={appearance.textMedium}>
          {formattedDate}
        </Typography>
        <Typography
          as="h1"
          size="wolf"
          weight="bold"
          className={clsx('whitespace-pre-line sp:whitespace-normal', appearance.textHigh)}
        >
          {greeting.greeting}
        </Typography>
        <Typography size="rabbit" className={appearance.textMedium} data-testid="home-encouragement">
          {greeting.encouragement}
        </Typography>
      </div>
      <Image
        src={appearance.icon}
        alt=""
        width={96}
        height={96}
        unoptimized
        className="size-20 min-w-20 sp:size-24 sp:min-w-24"
      />
    </div>
  );
};
